import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import SettingsSidebar from "../../../partials/settings/SettingsSidebar";
import { LoadingSpinner } from "../../../shared/components/LoadingSpinner";
import { useProfile } from "../../../shared/hooks/useProfile";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import { theme } from '@shared/utils/themeColors';
import { SubscriptionService } from "../services/subscription.service";

/**
 * Page for confirming subscription cancellation at the end of the billing period
 */
const SubscriptionCancelConfirmPage: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isLoading, error } = useProfile();
  const [isProcessing, setIsProcessing] = useState(false);

  // Cancel subscription at period end mutation
  const { mutate: cancelAtPeriodEnd } = useMutation({
    mutationFn: (subId: string) => SubscriptionService.cancelAtPeriodEnd(subId),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || "Successfully canceled subscription");
        queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });
        navigate("/settings/subscription-change/cancel/success");
      } else {
        toast.error(data.message || "Failed to cancel subscription");
        setIsProcessing(false);
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Error canceling subscription");
      setIsProcessing(false);
    },
  });

  const handleConfirmCancel = () => {
    if (!subscriptionId) {
      toast.error("Subscription ID is missing");
      return;
    }

    setIsProcessing(true);
    cancelAtPeriodEnd(subscriptionId);
  };

  const handleGoBack = () => {
    navigate("/settings/plans");
  };

  // Content for the cancel confirmation
  const renderCancelConfirmContent = () => {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
            <svg
              className="w-8 h-8 text-yellow-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h2 className={`text-2xl font-bold text-center ${theme.text.primary} mb-4`}>
            Cancel Subscription
          </h2>

          <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
            Are you sure you want to cancel your subscription? You'll continue
            to have access to premium features until the end of your current
            billing period.
          </p>

          <div className="space-y-4">
            <button
              onClick={handleConfirmCancel}
              disabled={isProcessing}
              className={`w-full py-2 px-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition-colors ${
                isProcessing ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isProcessing ? "Processing..." : "Yes, Cancel My Subscription"}
            </button>

            <button
              onClick={handleGoBack}
              disabled={isProcessing}
              className={`w-full py-2 px-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-colors ${
                isProcessing ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              No, Keep My Subscription
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Page header */}
        <div className="mb-8">
          {/* Title */}
          <h1 className={`text-2xl md:text-3xl ${theme.text.primary} font-bold`}>
            Account Settings
          </h1>
        </div>

        {/* Content */}
        <div className={`${theme.bg.primary} shadow-sm rounded-xl mb-8`}>
          <div className="flex flex-col md:flex-row md:-mr-px">
            <SettingsSidebar />
            <div className="grow">
              {isLoading ? (
                <div className="flex items-center justify-center w-full p-8">
                  <LoadingSpinner />
                </div>
              ) : error ? (
                <div className={`p-8 text-center ${theme.text.error}`}>
                  Error loading user data. Please try again.
                </div>
              ) : (
                renderCancelConfirmContent()
              )}
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default SubscriptionCancelConfirmPage;
